import { ConsultiveAccessService } from "../../presentation/services/ConsultiveAccessService";
import {
    HomeRedirectService,
    HOME_STATE_WITHOUT_ACCESS
} from "../../presentation/services/HomeRedirectService";
import { RolesUtils } from "../roles/roles";
import {
    accountingUsers,
    accoutingTrialUsers,
    customerUsers,
    nucontersUsers
} from "../users-roles/users-roles";

export const REPORTS_STATES = [
    "nucont.reports",
    "nucont.reports.company",
    "nucont.reports.company.visualization",
    "nucont.reports.company.rules",
    "nucont.reports.company.dre",
    "nucont.reports.company.dre.comparation",
    "nucont.reports.company.balance",
    "nucont.reports.company.balance.comparation",
    "nucont.reports.company.projection",
    "nucont.reports.company.budget",
    "nucont.reports.group",
    "nucont.reports.group.visualization",
    "nucont.reports.group.rules",
    "nucont.reports.group.dre",
    "nucont.reports.group.dre.comparation",
    "nucont.reports.group.balance",
    "nucont.reports.group.balance.comparation",
    "nucont.reports.group.projection",
    "nucont.reports.group.budget"
];

/**
 * Abertas a qualquer pessoa, logada ou não e de qualquer perfil: não passam pelas regras de
 * acesso (cliente logado em `/politica-de-privacidade` seria barrado por não estar numa
 * contabilidade).
 */
export const OPEN_STATES = ["nucont.privacyPolicy"];

export function isOpenState(stateName) {
    return OPEN_STATES.includes(stateName);
}

export const PUBLIC_STATES = [
    ...OPEN_STATES,
    "nucont.authentication",
    "nucont.signup-employee",
    "nucont.reportLogin",
    "nucont.npsform",
    ...REPORTS_STATES
];

export function userCanAccessState({ user, transition }) {
    if (nucontersUsers.includes(user.role)) return true;
    else {
        const params = transition.params();
        const canAccessCompany = userCanAccessCompany({
            user,
            params,
            stateName: transition.to().name
        });
        const requiredRoles = transition.to()?.data?.rolesGuard;
        const hasRequiredRoles = userHasRequiredRoles({ user, requiredRoles });
        return hasRequiredRoles && canAccessCompany;
    }
}

const userHasRequiredRoles = ({ user, requiredRoles }) => {
    if (!requiredRoles?.length) return true;
    return RolesUtils.userIsInRole({ user, roles: requiredRoles });
};

const userCanAccessCompany = ({ user, params, stateName }) => {
    if (customerUsers.includes(user.role)) {
        if (params.accountingId === user.profile.accountingId) {
            if (params.id) {
                if (
                    params.id === "EMPRESA_MODELO" ||
                    [
                        "Contabilidade",
                        "ContabilidadeTrial",
                        "ContabilidadeTrialPro"
                    ].includes(user.role) ||
                    stateName.indexOf("group") > -1
                )
                    return true;
                if (user.profile.companyIds.includes(params.id)) return true;
                else {
                    return false;
                }
            } else {
                return true;
            }
        } else {
            return false;
        }
    } else return true;
};

export function resolveRedirectWhenAccessDenied({
    user,
    transition,
    JWTService
}) {
    if ([...accountingUsers, ...accoutingTrialUsers].includes(user.role)) {
        // Sem Agente Consultivo o destino é dentro do V1, então dá para usar `target()` e
        // evitar o recarregamento de página que o `window.location.href` obriga.
        if (!ConsultiveAccessService.peek()) {
            return transition.router.stateService.target(
                HOME_STATE_WITHOUT_ACCESS,
                { accountingId: user.profile.accountingId }
            );
        }
        return _toRedirectV2(user.profile.accountingId);
    }
    if (user.role === "Empresa") {
        const companyIds = [...new Set(user?.profile?.companyIds)];
        if (companyIds.length > 1 || user.profile?.groupIds?.length) {
            return transition.router.stateService.target(
                "nucont.accounting.overview.portfolio.companies.register",
                { accountingId: user.profile.accountingId }
            );
        }
        return transition.router.stateService.target(
            "nucont.accounting.company",
            {
                id: companyIds[0],
                accountingId: user.profile.accountingId
            }
        );
    }
    return JWTService.signout();
}

export function isPublicState(stateName) {
    return PUBLIC_STATES.includes(stateName);
}

export function shouldSignoutAnonimo({ user, transition }) {
    if (!user?.profile?.isAnonimo) return false;
    return !REPORTS_STATES.includes(transition.to().name);
}

export function redirectAuthenticatedFromAuth({ user, transition }) {
    if (transition.to().name !== "nucont.authentication") return null;
    if (user.role === "Super")
        return transition.router.stateService.target(
            "nucont.accountings.customers"
        );
    if (user.role === "Empresa") {
        return transition.router.stateService.target(
            "nucont.accounting.overview.portfolio.companies.register",
            { accountingId: user.profile.accountingId }
        );
    } else {
        if (!ConsultiveAccessService.peek()) {
            return transition.router.stateService.target(HOME_STATE_WITHOUT_ACCESS, {
                accountingId: user.profile.accountingId
            });
        }
        window.location.href = HomeRedirectService.getUrl({
            accountingId: user.profile.accountingId,
            hasAccess: true
        });
    }
}

export function _toRedirectV2(accountingId) {
    HomeRedirectService.go({ accountingId, hasAccess: true });
}

export function redirectUnauthenticatedToAuth({ transition }) {
    if (transition.to().name === "nucont.authentication") return null;
    return transition.router.stateService.target("nucont.authentication", {
        redirectToStateName: transition.to().name,
        redirectToStateParams: transition.params()
    });
}
